import { HStack, Divider, Box, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { Button, IconButton } from 'Atoms';
import { TeamMember, TeamMembersFilterOption, useTeamMembers } from 'containers/TeamMembers';
import { TaskStatusEnum_Enum_ } from 'models';
import { Menu, MenuAction } from 'Molecules/Menu';
import { Modal } from 'Molecules/Modal';
import { SearchInput } from 'Molecules/SearchInput';
import { UserAvatar } from 'Organisms';
import { useState } from 'react';
import { Typography } from 'Tokens';
import { CalendarIcon, ListDetailsIcon } from 'Tokens/Icons/Data';
import { AddUserIcon, FileDescriptionIcon, MoreIcon, RemoveIcon } from 'Tokens/Icons/Function';
import { CheckIcon, LoaderIcon } from 'Tokens/Icons/Status';
import { TASK_STATUS } from './TaskToolbar.utils';
import { DeadlinePicker } from 'Molecules/DeadlinePicker';

export type HandleTaskUpdate = (
  type: 'Responsible' | 'Deadline' | 'Status',
  data: {
    responsible?: string;
    deadline?: Date;
    status?: TaskStatusEnum_Enum_;
  }
) => Promise<any>;

export const ToolbarToast = ({
  text,
  isStandalone,
  visible,
  isLoading,
}: {
  text: string;
  isStandalone?: boolean;
  visible: boolean;
  isLoading: boolean;
}) => {
  return (
    <Box
      position="absolute"
      bg="bg.accent"
      borderRadius="8px"
      bottom={isStandalone ? '40px' : '65px'}
      left="50%"
      opacity={visible ? 1 : 0}
      transformOrigin="center"
      w="fit-content"
      transition="all 0.25s ease-out"
      transform={visible ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.95)'}
      zIndex={99}
    >
      <HStack gap="8px" p="12px">
        {isLoading ? <LoaderIcon color="text.onAccent" /> : <CheckIcon color="text.onAccent" />}
        <Typography variant="body" color="text.onAccent" whiteSpace="nowrap">
          {text}
        </Typography>
      </HStack>
    </Box>
  );
};

const ToolbarDivider = () => {
  return (
    <Divider
      orientation="vertical"
      border="1px"
      borderRadius="3px"
      height="20px"
      borderColor="border.onAccent"
      backgroundColor="border.onAccent"
    />
  );
};

export const ResponsibleSelector = ({
  isOpen,
  onClose,
  onSelect,
  handleTaskUpdate,
  isStandalone,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (ownerId: string, ownerName?: string) => void;
  handleTaskUpdate?: HandleTaskUpdate;
  isStandalone?: boolean;
}) => {
  const { members } = useTeamMembers(TeamMembersFilterOption.Active);
  const [search, setSearch] = useState<string>('');
  const toast = useToast();
  const { user } = useAuth();
  const handleSearchMembers = (member: TeamMember) => {
    return (
      member.name?.toLowerCase().includes(search.toLowerCase()) ||
      member.email?.toLowerCase().includes(search.toLowerCase())
    );
  };
  const [toastVisible, setToastVisible] = useState(false);
  const [toastText, setToastText] = useState('');
  const [isToastLoading, setIsToastLoading] = useState(false);

  return (
    <>
      {!isStandalone && (
        <ToolbarToast
          text={toastText}
          visible={toastVisible}
          isStandalone={isStandalone}
          isLoading={isToastLoading}
        />
      )}

      <Modal isOpen={isOpen} onClose={onClose} hasHeader={false} hasFooter={false}>
        <VStack gap="8px" alignItems="stretch">
          <SearchInput
            width="100%"
            placeholder="Search"
            search={search}
            setSearch={setSearch}
            marginBottom="4px"
          />
          <Typography variant="detail" mt="8px" ml="8px">
            Assign task
          </Typography>
          <Box maxH="200px" overflowY="auto">
            {members?.filter(handleSearchMembers).map((member) => (
              <HStack
                key={member.id}
                p="8px"
                m="0px"
                cursor="pointer"
                height="36px"
                _hover={{ backgroundColor: 'bg.hover' }}
                borderRadius="8px"
                w="100%"
                onClick={() => {
                  setIsToastLoading(true);
                  setToastText(`Assigning responsible...`);
                  setToastVisible(true);
                  if (onSelect) onSelect(member.id ?? '', member.name);
                  else if (handleTaskUpdate) {
                    handleTaskUpdate('Responsible', {
                      responsible: member.id,
                    }).then(() => {
                      setIsToastLoading(false);
                      setToastVisible(false);
                      setToastText(`Assigned ${member.name} as responsible`);
                      setToastVisible(true);
                      setTimeout(() => setToastVisible(false), 3000);
                    });
                  }
                  onClose();
                }}
              >
                <HStack w="100%" spacing="8px">
                  <UserAvatar user={member.user} size="sm" />
                  <Typography variant="bodyStrong" noOfLines={1}>
                    {`${member.name ?? member.email} ${user?.email === member.email ? '(You)' : ''}`}
                    {member.pending && '(Pending)'}
                  </Typography>
                </HStack>
              </HStack>
            ))}
          </Box>
        </VStack>
      </Modal>
    </>
  );
};

export const TaskToolbar = ({
  selectOptions,
  selectedText,
  onClose,
  handleTaskUpdate,
  hideStatus = false,
  onOptOutClick,
  showOptOutOption,
}: {
  selectOptions: MenuAction[];
  selectedText: string;
  onClose: () => void;
  handleTaskUpdate: HandleTaskUpdate;
  hideStatus?: boolean;
  onOptOutClick?: () => void;
  showOptOutOption?: boolean;
}) => {
  const {
    isOpen: isResponsibleSelectorOpen,
    onClose: onCloseResponsibleSelector,
    onToggle: toggleResponsibleSelector,
  } = useDisclosure();
  const {
    isOpen: isDeadlinePickerOpen,
    onToggle: onToggleDeadlinePicker,
    onClose: onCloseDeadlinePicker,
  } = useDisclosure();
  const toast = useToast();
  const [toastVisible, setToastVisible] = useState(false);
  const [toastText, setToastText] = useState('');
  const [isToastLoading, setIsToastLoading] = useState(false);

  return (
    <HStack
      position="absolute"
      bg="bg.accent"
      justifyContent="space-between"
      color="text.onAccent"
      borderRadius="8px"
      paddingX="12px"
      paddingY="8px"
      bottom="4%"
      left="50%"
      transform="translateX(-50%)"
      zIndex={1}
    >
      <HStack spacing="8px">
        <Typography variant="bodyStrong" color="text.onAccent" whiteSpace="nowrap">
          {selectedText}
        </Typography>
        <ToolbarDivider />
        <Menu
          menuButton={
            <Button
              color="text.onAccent"
              variant="ghost"
              size="sm"
              leftIcon={<ListDetailsIcon color="text.onAccent" />}
              _hover={{ backgroundColor: 'bg.onAccent.hover' }}
            >
              Select
            </Button>
          }
          placement="top-end"
          menuListMargin="0 0 10px 0px"
          usePortal
          sections={[
            {
              title: 'Select all',
              actions: selectOptions,
            },
          ]}
        />
        <Button
          color="text.onAccent"
          variant="ghost"
          size="sm"
          onClick={toggleResponsibleSelector}
          leftIcon={<AddUserIcon color="text.onAccent" />}
          _hover={{ backgroundColor: 'bg.onAccent.hover' }}
        >
          Assign
        </Button>
        <Button
          color="text.onAccent"
          variant="ghost"
          size="sm"
          onClick={onToggleDeadlinePicker}
          leftIcon={<CalendarIcon color="text.onAccent" />}
          _hover={{ backgroundColor: 'bg.onAccent.hover' }}
        >
          Set deadline
        </Button>
        {!hideStatus && (
          <Menu
            menuButton={
              <Button
                color="text.onAccent"
                variant="ghost"
                size="sm"
                leftIcon={<CheckIcon color="text.onAccent" />}
                _hover={{ backgroundColor: 'bg.onAccent.hover' }}
              >
                Change status
              </Button>
            }
            placement="top-end"
            menuListMargin="0 0 10px 0px"
            usePortal
            sections={[
              {
                title: 'Change task status',
                actions: Object.values(TaskStatusEnum_Enum_).map((value) => {
                  const Icon = TASK_STATUS[value].icon;
                  return {
                    title: TASK_STATUS[value].title,
                    onClick: () => {
                      setToastText(`Updating status...`);
                      setToastVisible(true);
                      handleTaskUpdate('Status', { status: value }).then(() => {
                        setToastVisible(false);
                        setToastText(`Status updated`);
                        setToastVisible(true);
                        setTimeout(() => setToastVisible(false), 3000);
                      });
                    },
                    id: value,
                    leftElement: <Icon />,
                  };
                }),
              },
            ]}
          />
        )}
        {!!onOptOutClick && (
          <Menu
            placement="top-end"
            menuListMargin="0 0 10px 0px"
            menuButton={
              <IconButton
                aria-label="edit-dr-owner"
                icon={<MoreIcon color="text.onAccent" alignSelf="center" />}
                borderRadius="50%"
                variant="ghost"
                size="sm"
              />
            }
            sections={[
              {
                title: 'Change relevancy',
                actions: [
                  {
                    title: showOptOutOption ? 'Mark as irrelevant' : 'Start reporting',
                    onClick: () => {
                      onOptOutClick();
                    },
                    id: 'markAsIrrelevant',
                    leftElement: showOptOutOption ? <RemoveIcon /> : <FileDescriptionIcon />,
                  },
                ],
              },
            ]}
          />
        )}
        <ToolbarDivider />
        <IconButton
          color="text.onAccent"
          aria-label="ss"
          variant="ghost"
          icon={<RemoveIcon color="inherit" />}
          onClick={onClose}
        />
      </HStack>
      <ToolbarToast text={toastText} visible={toastVisible} isLoading={isToastLoading} />

      <ResponsibleSelector
        isOpen={isResponsibleSelectorOpen}
        onClose={onCloseResponsibleSelector}
        handleTaskUpdate={handleTaskUpdate}
      />
      <DeadlinePicker
        onClose={onCloseDeadlinePicker}
        isOpen={isDeadlinePickerOpen}
        onChange={(date: Date) => {
          setIsToastLoading(true);
          setToastText(`Setting due date...`);
          setToastVisible(true);
          handleTaskUpdate('Deadline', { deadline: date }).then(() => {
            setIsToastLoading(false);
            setToastVisible(false);
            setToastText(
              `Due date set to ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
            );
            setToastVisible(true);
            setTimeout(() => setToastVisible(false), 3000);
          });
        }}
      />
    </HStack>
  );
};
